import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    Calendar,
    GraduationCap,
    Plus,
    FolderOpen,
    Settings,
    LogOut,
    User,
    ChevronRight,
    AlertTriangle,
    Wrench,
    RefreshCw,
    FileText,
    X,
    Wallet,
    Clock
} from 'lucide-react';

const navItems = [
    { to: '/user', icon: Calendar, label: 'Calendario', exact: true, permission: 'calendario.ver' },
    { to: '/user/ope', icon: GraduationCap, label: 'OPE', permission: 'ope.ver' },
    { id: 'actions', icon: Plus, label: 'Acciones', isAction: true },
    { to: '/user/historial', icon: FolderOpen, label: 'Historial', permission: 'intercambios.ver' },
    { to: '/user/ajustes', icon: Settings, label: 'Ajustes' }, // Ajustes suele ser libre
];

const actionItems = [
    { id: 'accidente', icon: AlertTriangle, label: 'Accidente', color: 'from-red-500 to-red-600', permission: 'accidentes.ver' },
    { id: 'averia', icon: Wrench, label: 'Avería', color: 'from-orange-500 to-orange-600', permission: 'averias.ver' },
    { id: 'cambio', icon: RefreshCw, label: 'Cambio', color: 'from-blue-500 to-blue-600', permission: 'intercambios.ver' },
    { id: 'ap', icon: FileText, label: 'A.P.', color: 'from-green-500 to-green-600', permission: 'asuntos_propios.ver' },
    { id: 'bolsa', icon: Wallet, label: 'Bolsa Días', color: 'from-purple-500 to-indigo-600', permission: 'calendario.ver' },
    { id: 'horas-sindicales', icon: Clock, label: 'H. Sindical', color: 'from-cyan-500 to-teal-600', permission: 'calendario.ver' },
];

import AvatarCom from '../Avatar';

const Sidebar = () => {
    const { user, logout, hasPermission, isAdmin } = useAuth();
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const navigate = useNavigate();

    // Determinar si es admin para bypass de permisos
    const isUserAdmin = typeof isAdmin === 'function' ? isAdmin() : false;

    // Helper para filtrar por permisos
    const filterByPermission = (items) => {
        if (isUserAdmin) return items;
        return items.filter(item => {
            if (!item.permission) return true;
            return hasPermission(item.permission);
        });
    };

    const visibleNavItems = filterByPermission(navItems);
    const visibleActionItems = filterByPermission(actionItems);

    const handleAction = (actionId) => {
        setIsActionsOpen(false);

        // Navigate to A.P. page
        if (actionId === 'ap') {
            navigate('/user/ap');
            return;
        }

        if (actionId === 'cambio') {
            navigate('/user/cambios');
            return;
        }

        if (actionId === 'averia') {
            navigate('/user/averia');
            return;
        }

        if (actionId === 'accidente') {
            navigate('/user/accidentes');
            return;
        }

        if (actionId === 'bolsa') {
            navigate('/user/bolsa-dias');
            return;
        }

        if (actionId === 'horas-sindicales') {
            navigate('/user/horas-sindicales');
            return;
        }
    };

    return (
        <>
            {/* Actions Modal */}
            {isActionsOpen && visibleActionItems.length > 0 && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                        onClick={() => setIsActionsOpen(false)}
                    />

                    {/* Actions Panel - Positioned near sidebar */}
                    <div className="fixed top-1/2 left-64 -translate-y-1/2 z-50 ml-4">
                        <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-5 w-80 shadow-2xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Acciones rápidas</h3>
                                <button
                                    onClick={() => setIsActionsOpen(false)}
                                    className="p-1 rounded-full hover:bg-[var(--bg-card-hover)] transition-colors"
                                >
                                    <X className="w-5 h-5 text-[var(--text-muted)]" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {visibleActionItems.map((action) => (
                                    <button
                                        key={action.id}
                                        onClick={() => handleAction(action.id)}
                                        className={`flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br ${action.color} transition-transform hover:scale-105 active:scale-95`}
                                    >
                                        <action.icon className="w-5 h-5 text-white" />
                                        <span className="text-sm font-medium text-white">{action.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            <aside className="hidden md:flex flex-col w-64 border-r min-h-screen" style={{ background: 'var(--sidebar-bg)', borderColor: 'var(--border-color)' }}>
                {/* Logo/Brand */}
                <div className="p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xl">
                            🚌
                        </div>
                        <div>
                            <h1 className="font-bold" style={{ color: 'var(--text-primary)' }}>TUVISA</h1>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Mi Calendario</p>
                        </div>
                    </div>
                </div>

                {/* User Info */}
                <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--bg-card)' }}>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center overflow-hidden">
                            <AvatarCom 
                                src={user?.picture} 
                                name={user?.conductorNombre || user?.nombre}
                                alt="Avatar" 
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{user?.conductorNombre || user?.nombre || 'Usuario'}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Conductor</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-1">
                    {visibleNavItems.map((item) => (
                        item.isAction ? (
                            visibleActionItems.length > 0 && (
                                <button
                                    key={item.id}
                                    onClick={() => setIsActionsOpen(!isActionsOpen)}
                                    className={`
                                        flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 w-full
                                        ${isActionsOpen
                                            ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                                            : 'border border-transparent'
                                        }
                                    `}
                                    style={{ 
                                        color: isActionsOpen ? 'var(--accent-label)' : 'var(--text-secondary)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActionsOpen) {
                                            e.currentTarget.style.backgroundColor = 'var(--bg-card-hover)';
                                            e.currentTarget.style.color = 'var(--text-primary)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActionsOpen) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = 'var(--text-secondary)';
                                        }
                                    }}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                    <ChevronRight className={`w-4 h-4 ml-auto opacity-50 transition-transform ${isActionsOpen ? 'rotate-90' : ''}`} />
                                </button>
                            )
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.exact}
                                className={({ isActive }) => `
                                    flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                                    ${isActive
                                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                                        : 'border border-transparent'
                                    }
                                `}
                                style={({ isActive }) => ({ 
                                    color: isActive ? 'var(--accent-label)' : 'var(--text-secondary)' 
                                })}
                                onMouseEnter={(e) => {
                                    const isActive = e.currentTarget.classList.contains('active');
                                    if (!isActive) {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-card-hover)';
                                        e.currentTarget.style.color = 'var(--text-primary)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    const isActive = e.currentTarget.classList.contains('active');
                                    if (!isActive) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'var(--text-secondary)';
                                    }
                                }}
                            >
                                <item.icon className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                                <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                            </NavLink>
                        )
                    ))}
                </nav>

                {/* Logout */}
                <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <button
                        onClick={logout}
                        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                            e.currentTarget.style.color = '#f87171';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                    >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Cerrar Sesión</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
